/**
 * The Reports view, as pure helpers: the plain controls' options, what counts
 * as a feed filter, and the client-side regrouping of the feature matrix. No React, so CRA's jest can test all of it.
 *
 * Reports are the room's front door for people who do not think in facets:
 * the rail is powerful and most people do not know what to do with it, so a
 * room opens on a table that reads without a filter, and the feed with its rail waits
 * behind a Filters toggle for the people who want it.
 */
import type { TabDef } from '../../common/Tabs';
import type { BoardroomFeatureMatrix, BoardroomMatrixGroup, BoardroomReportKey } from '../../../types';
import type { View, VerifiedFilter } from '../filters';
import { tagTerms } from '../filters';
import type { BoardroomCardLens } from '../../../types';
import { seatRole } from '../plainWords';

type Lens = Pick<View, 'tags' | 'verified' | 'sourced' | 'by'>;

// THE BOARD IS THE ROOM'S FRONT DOOR: the tables this room chose, pinned where
// its owner put them. `reports` is still every table, reachable by opening one
// from the board; the feed is still every item, behind the left edge.
export type Stage = 'board' | 'reports' | 'feed' | 'profile';
/** The kinds that have a profile page; everything else opens in the drawer. */
export const PROFILE_KINDS = ['COMPANY', 'PRODUCT', 'SOLUTION_TYPE', 'FEATURE'] as const;

export const MATRIX_GROUP_KEY = 'jcseo.boardroom.matrix.group';

function stored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remember(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private mode, blocked storage: the default is fine */
  }
}

/** A feed-only filter is set: a bucket, a lookup state or a lookup kind. The
 *  lens (tags, validation, provenance, author) is not — it narrows the reports
 *  too, and shows as chips wherever you are. */
export function feedFilterOn(view: Pick<View, 'filter' | 'lookup' | 'kind'>): boolean {
  return Boolean(view.filter || view.lookup || view.kind);
}

// Which way the matrix reads used to live here, as a single setting shared by
// every table in the room. It is `DataTable`'s own flip now, remembered per
// table — a reader who turns the spec sheet has not asked to turn the
// feature matrix too.

export type MatrixGrouping = 'type' | 'company';
export function storedMatrixGrouping(): MatrixGrouping {
  return stored(MATRIX_GROUP_KEY) === 'company' ? 'company' : 'type';
}
export const rememberMatrixGrouping = (g: MatrixGrouping) => remember(MATRIX_GROUP_KEY, g);

/** The kinds of card that are one of many, told apart by their subject: a
 *  table or report the chat built, or one company's brand searches. Each gets
 *  its own tab and fetches for itself. */
export type CardKind = 'list' | 'report' | 'brand';
export const CARD_KINDS: readonly string[] = ['list', 'report', 'brand'];
export const isCardKind = (k: string | null | undefined): k is CardKind => CARD_KINDS.includes(k ?? '');

/** A CUT: one of the room's tables with a filter of its own (a tag, say), as
 *  a noticing puts it on the board. A table's kind plus a subject. It gets its
 *  own tab like a chat-built card, and draws the table it is a cut of. */
export function isCut(d: { kind: string; subject?: string | null }): boolean {
  return !!d.subject && REPORT_TABS.some(t => t.key === d.kind);
}

/** What a cut's table is fetched with: the lens you have on, plus the cut's
 *  own filter. Yours wins where both set the same thing. Pure. */
export function cutLens(view: Lens, cut: BoardroomCardLens | null | undefined): Lens {
  if (!cut) return view;
  return {
    tags: [...view.tags, ...(cut.tags ?? []).filter(t => !view.tags.includes(t))],
    verified: view.verified ?? ((cut.verified as VerifiedFilter | null | undefined) ?? null),
    sourced: view.sourced ?? ((cut.sourced as Lens['sourced'] | undefined) ?? null),
    by: view.by ?? cut.by ?? null,
  };
}

/** "content: tool list", for saying what a cut keeps. Pure. */
export function cutWords(cut: BoardroomCardLens | null | undefined): string {
  return (cut?.tags ?? []).map(t => t.replace(/_/g, ' ').replace(':', ': ').toLowerCase()).join(' · ');
}

export const REPORT_TABS: TabDef<BoardroomReportKey>[] = [
  { key: 'who_ranks', label: 'Who ranks', hint: 'Which sites keep coming up in this room\u2019s own searches, and where' },
  { key: 'competitors', label: 'Competitors', hint: 'Every company, what it sells, and what nobody has looked at yet' },
  { key: 'feature_matrix', label: 'Feature matrix', hint: 'Who ships what, and behind which plan' },
  { key: 'feature_cloud', label: 'Feature cloud', hint: 'Which features most products ship, and which only a few do' },
  { key: 'price_map', label: 'Price map', hint: 'What each product costs, by solution type' },
  { key: 'email_capture', label: 'Email capture', hint: 'How each company asks for an email address, through which provider, and where' },
  { key: 'appeal', label: 'Appeal', hint: 'How each company and each of its pages sells: who for, the angle it leads with, and its proof' },
  { key: 'contacts', label: 'Contacts', hint: 'How to reach each company: email, phone, contact page, socials and chat' },
  { key: 'demand', label: 'Demand', hint: 'What buyers search for, how often, and which way it is moving' },
  { key: 'search_market', label: 'Search market', hint: 'Every term buyers search here, and who holds those searches' },
  { key: 'brand_grid', label: 'Who goes after whom', hint: 'Which rivals show up on each company’s own searches, and how much' },
  { key: 'landscape', label: 'Landscape', hint: 'Who is here, crossed by two of the room\'s own tag groups' },
  { key: 'pain_gap', label: 'Pain gap', hint: 'Which complaints nobody addresses' },
  { key: 'audience', label: 'Where they talk', hint: 'Where people in this market post and talk, ranked for joining in' },
  // "Openings" is what the server's catalogue calls it, and therefore what the
  // board and the browser say. It read 'Opportunities' here for no reason but
  // that two lists of names were written months apart.
  { key: 'opportunity_brief', label: 'Openings', hint: 'Each idea with its pains, numbers and competitors' },
  // A story room's tables.
  { key: 'story', label: 'Story so far', hint: 'What happened and when, who it involved, and why it is news now' },
  { key: 'people', label: 'Who', hint: 'The people in the story and what each said on the record' },
  { key: 'angles', label: 'Angles to write', hint: 'What people search about this that nobody answers well' },
  // LAST ON THE STRIP, because it is what the room is NOT about. It earns its
  // place there rather than being hidden: a room that has drifted needs the
  // way to say so somewhere a reader will find it.
  { key: 'related', label: 'Related', hint: 'What the room turned up that is not the subject - and where you sort it' },
  // Recon runs is not a tab: it is a panel on the room's right-hand rail.
];

/** The item a group heading stands for: the solution type, or the company
 *  behind a "co:<id>" key from regroupByCompany. Null for the leftover group. */
export function groupItemId(group: { key: string; solution_type_id: number | null }): number | null {
  if (group.solution_type_id) return group.solution_type_id;
  if (group.key.startsWith('co:')) {
    const id = Number(group.key.slice(3));
    return Number.isInteger(id) && id > 0 ? id : null;
  }
  return null;
}

const TABLE_STAKES_FRAC = 2 / 3;

type MatrixCell = BoardroomMatrixGroup['rows'][number]['cells'][string];
const NONE_CELL: MatrixCell = { state: 'none' };
const UNREAD_CELL: MatrixCell = { state: 'unread' };

/** One cell of the feature matrix. The server sends only the cells that say
 *  something (ships, or ships on a plan); an empty one is "none" when the
 *  product's pages were read and "unread" when they were not. Pure. */
export function matrixCell(
  cells: Record<string, MatrixCell>,
  product: { id: number; read?: boolean },
): MatrixCell {
  return cells[String(product.id)] ?? (product.read ? NONE_CELL : UNREAD_CELL);
}

/** The same matrix with columns grouped by COMPANY instead of solution type.
 *  Rows are recomputed per group from the cells, so "table stakes" means
 *  table stakes within that company's products. */
export function regroupByCompany(data: BoardroomFeatureMatrix): BoardroomMatrixGroup[] {
  const products = data.groups.flatMap(g => g.products);
  const rowsByFeature = new Map<number, { title: string; verdict: BoardroomMatrixGroup['rows'][number]['verdict']; cells: Record<string, BoardroomMatrixGroup['rows'][number]['cells'][string]> }>();
  for (const g of data.groups) {
    for (const r of g.rows) {
      const cur = rowsByFeature.get(r.feature_id) ?? { title: r.title, verdict: r.verdict, cells: {} };
      Object.assign(cur.cells, r.cells);
      rowsByFeature.set(r.feature_id, cur);
    }
  }
  const groups = new Map<string, BoardroomMatrixGroup>();
  for (const p of products) {
    const key = p.company ? `co:${p.company.id}` : 'none';
    const label = p.company ? p.company.title : 'No company recorded';
    const g = groups.get(key) ?? { key, solution_type_id: null, label, products: [], rows: [], dig: null };
    g.products.push(p);
    groups.set(key, g);
  }
  for (const g of Array.from(groups.values())) {
    const field = g.products.length;
    g.rows = [];
    for (const [fid, r] of Array.from(rowsByFeature.entries())) {
      // Only the cells that say something, as the server sends them.
      const cells: BoardroomMatrixGroup['rows'][number]['cells'] = {};
      let ships = 0;
      let gated = 0;
      for (const p of g.products) {
        const c = r.cells[String(p.id)];
        if (!c || (c.state !== 'ships' && c.state !== 'gated')) continue;
        cells[String(p.id)] = c;
        ships += 1;
        if (c.state === 'gated') gated += 1;
      }
      if (ships === 0) continue;
      g.rows.push({
        feature_id: fid,
        title: r.title,
        verdict: r.verdict,
        ships,
        field_size: field,
        table_stakes: ships / field >= TABLE_STAKES_FRAC,
        gated,
        cells,
      });
    }
    g.rows.sort((a, b) => b.ships - a.ships || a.title.localeCompare(b.title));
  }
  // A company group's dig is the table's dig narrowed to its products: the
  // directive itself is server-built, so here the count is what changes and
  // the whole-table directive is reused when any product in the group has a hole.
  for (const g of Array.from(groups.values())) {
    const ids = new Set(g.products.map(p => p.id));
    const holes = data.missing.filter(m => m.item_id !== null && ids.has(m.item_id));
    g.dig = data.dig && holes.length ? { ...data.dig, count: new Set(holes.map(m => m.item_id)).size } : null;
  }
  return Array.from(groups.values()).sort(
    (a, b) => Number(a.key === 'none') - Number(b.key === 'none') || b.products.length - a.products.length || a.label.localeCompare(b.label),
  );
}

/** A dig someone has lined up but not started: what the next press of Dig
 *  deeper will ask, who it wakes, and what it is confined to. Shown under the
 *  description so the change is visible before it costs anything. */
export interface StagedDig {
  directive: string;
  /** Comma-separated archetypes, or null for everyone. */
  archetype: string | null;
  item_id: number | null;
  scope: { tags: string[]; verified: string | null; by: string | null; tables?: string[] } | null;
  intent: 'WIDER' | 'DEEPER';
  /** Where it came from, for the panel's eyebrow. */
  source: string;
  /** One line of plain English for a dig — what it will
   *  work through, and who. A person reading the window should not have to
   *  read a prompt to know what they are about to buy. */
  plan?: string;
}

/**
 * What a dig is AIMED at, as the run wants it.
 *
 * AN ITEM IS A FOCUS ON ITS OWN. This used to require an archetype, so a dig
 * aimed at a row the room had already finished — nothing missing, therefore no
 * seat named — started unaimed at the whole board. The server drops an empty
 * archetype and keeps the item, so sending the item alone is the honest thing:
 * "look at this one", without prescribing who.
 */
export function digFocus(
  s: Pick<StagedDig, 'archetype' | 'item_id'>,
): { archetype: string; item_id: number | null } | null {
  if (!s.archetype && !s.item_id) return null;
  return { archetype: s.archetype ?? '', item_id: s.item_id };
}

export function scopeLabel(scope: StagedDig['scope']): string {
  if (!scope) return '';
  // In words, as the lens bar says it: "not product function: a or b". The
  // stored spelling ("!PRODUCT_FUNCTION:a|b") showed on Follow this lead.
  const bits = tagTerms(scope.tags).map(
    t => `${t.negated ? 'not ' : ''}${t.dimension.toLowerCase().replace(/_/g, ' ')}: ${t.values.join(' or ')}`,
  );
  // WHICH TABLE, first — it is the strongest thing a person said about a dig,
  // and the one they will check afterwards.
  const tables = (scope.tables ?? []).map(t => t.replace(/_/g, ' '));
  if (tables.length) bits.unshift(`filling ${tables.join(', ')}`);
  const names: Record<string, string> = { HOLDS: 'confident', DOES_NOT_HOLD: 'not confident', UNVERIFIABLE: 'unverifiable', UNCHECKED: 'not validated' };
  if (scope.verified && names[scope.verified]) bits.push(names[scope.verified]);
  if (scope.by) bits.push(scope.by === 'human' ? 'by you' : 'by one researcher');
  return bits.join(' · ');
}

export function whoLabel(archetype: string | null): string {
  if (!archetype) return 'everyone in the room';
  // What each seat does, not its name (see `plainWords`).
  return archetype
    .split(',')
    .map(a => a.trim())
    .filter(Boolean)
    .map(seatRole)
    .join(' and ');
}

/** Whether a dig is actually lined up — a directive, a lens or a seat. Only
 *  then does it earn a strip above the board. */
export function stagedHasContent(staged: StagedDig | null | undefined): boolean {
  return Boolean(staged && (staged.directive.trim() || staged.scope || staged.archetype));
}

